import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, ChevronDown, BookOpen, Compass, Target, TrendingUp, Users, Sparkles, GraduationCap, Heart, ExternalLink } from 'lucide-react';
import DependenciaTestModal from '../components/DependenciaTestModal';

const WSP_MESSAGE = 'Quiero saber más sobre el coaching en adicciones';
const WSP_URL = `https://wa.me/56939014212?text=${encodeURIComponent(WSP_MESSAGE)}`;

// Foto assets (CDN Emergent) — mapeo verificado
const IMG = {
  coffee: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_b3e01f14-9bf6-43e2-8803-5135c9820171/artifacts/v0dalnjm_WhatsApp%20Image%202026-08-18%20at%2018.19.44.jpeg',
  palmera: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_b3e01f14-9bf6-43e2-8803-5135c9820171/artifacts/gwg9epzc_WhatsApp%20Image%202026-08-18%20at%2018.17.41.jpeg',
  libro: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_b3e01f14-9bf6-43e2-8803-5135c9820171/artifacts/tn2rua11_7f64aa5m_libro%20portada.png',
  podcast: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_b3e01f14-9bf6-43e2-8803-5135c9820171/artifacts/8u2qdky1_WhatsApp%20Image%202026-08-18%20at%2018.29.07.jpeg',
  colegio: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_b3e01f14-9bf6-43e2-8803-5135c9820171/artifacts/j2q2s06h_WhatsApp%20Image%202026-08-18%20at%2018.31.10.jpeg',
  storyDark: '/coach/story-dark.png',
  storyLight: '/coach/story-light.png',
};

// Fade-in on scroll hook
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { threshold: 0.15, rootMargin: '0px 0px -50px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, visible };
}

const Reveal = ({ children, delay = 0, className = '' }) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default function CoachAdiccionesPage() {
  const [testOpen, setTestOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900 overflow-x-hidden" data-testid="coach-page">
      <title>Coaching en Adicciones · Nelson González · Chile</title>
      <meta name="description" content="Coach y acompañamiento para el cambio en adicciones. Nelson González — 20 años de consumo, 5 procesos en centros de rehabilitación, fundador de SinAdicciones.org. Programa El Primer Paso." />
      <meta name="keywords" content="coach adicciones chile, coaching adicciones, acompañamiento cambio hábitos, Nelson González, El Mapa Inverso, recuperación adicciones, coaching alcohol drogas" />
      <link rel="canonical" href="https://sinadicciones.org/coach-adicciones" />
      <meta property="og:title" content="Coaching en Adicciones · Nelson González" />
      <meta property="og:description" content="Un espacio para pasar de 'tengo que cambiar' a 'estoy empezando a cambiar'. 20 años de experiencia personal + coaching profesional." />
      <meta property="og:image" content={IMG.coffee} />
      <meta property="og:url" content="https://sinadicciones.org/coach-adicciones" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Person",
            "name": "Nelson González",
            "jobTitle": "Coach en Adicciones · Especialista en hábitos",
            "description": "Coach y autor de El Principio del Mapa Inverso. Fundador de SinAdicciones.org. 20 años de consumo, 5 procesos en centros de rehabilitación. Colaborador SENDA Previene Puente Alto.",
            "worksFor": { "@type": "Organization", "name": "SinAdicciones.org", "url": "https://sinadicciones.org" },
            "url": "https://sinadicciones.org/coach-adicciones",
            "image": IMG.coffee
          },
          {
            "@type": "Service",
            "name": "El Primer Paso · Programa de Coaching",
            "serviceType": "Coaching en adicciones",
            "provider": { "@type": "Person", "name": "Nelson González" },
            "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "url": "https://sinadicciones.org/coach-adicciones" },
            "description": "Programa de coaching, acompañamiento y educación para pasar de 'tengo que cambiar' a 'estoy empezando a cambiar'."
          }
        ]
      })}} />

      <CoachHeader onCta={() => window.open(WSP_URL, '_blank', 'noreferrer')} />

      {/* ============ ACTO I — EL BUCLE ============ */}
      <section className="relative min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${IMG.storyDark})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/70 via-neutral-950/50 to-neutral-950" />
        <div className="relative max-w-3xl mx-auto px-6 py-24 text-center">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-[10px] text-neutral-500 font-sans mb-8">Coach Adicciones · Chile</p>
          </Reveal>
          <Reveal delay={200}>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif font-light leading-[1.15] mb-8" data-testid="hero-title">
              Sabes que algo<br /><em className="text-amber-400 not-italic font-normal">tiene que cambiar.</em>
            </h1>
          </Reveal>
          <Reveal delay={500}>
            <p className="text-xl md:text-2xl text-neutral-400 font-serif italic mb-16">Pero todavía sigues ahí.</p>
          </Reveal>
          <Reveal delay={900}>
            <div className="inline-flex flex-col items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest font-sans animate-pulse">
              <span>Desliza</span>
              <ChevronDown className="w-4 h-4" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ ACTO II — LAS PROMESAS ============ */}
      <section className="bg-neutral-900 text-neutral-200 py-32 px-6">
        <div className="max-w-2xl mx-auto space-y-16 font-serif">
          {[
            'Dijiste que sería la última vez.',
            'Y probablemente lo creías.',
            ['Esta noche no.', 'Este fin de semana no.', 'El lunes paro.', 'Cuando termine esto.', 'Cuando esté más tranquilo.', 'Cuando tenga una razón.'],
            ['Pero vuelve a pasar.', 'Una noche.', 'Otra promesa.', 'Otra culpa.', 'Otro intento.', 'Otra vez desde cero.'],
          ].map((block, i) => (
            <Reveal key={i} delay={i * 100}>
              {Array.isArray(block) ? (
                <div className="space-y-3 text-neutral-500 text-lg md:text-xl italic pl-6 border-l-2 border-amber-600/40">
                  {block.map((line, j) => <p key={j}>{line}</p>)}
                </div>
              ) : (
                <p className="text-2xl md:text-3xl font-light">{block}</p>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ ACTO III — EL GIRO ============ */}
      <section className="relative bg-white text-neutral-900 py-32 px-6 overflow-hidden">
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-full opacity-15 hidden md:block"
          style={{
            backgroundImage: `url(${IMG.storyLight})`,
            backgroundSize: 'cover',
            backgroundPosition: 'left center',
          }}
        />
        <div className="relative max-w-3xl mx-auto font-serif">
          <Reveal>
            <p className="text-2xl md:text-3xl font-light leading-relaxed mb-8">Hasta que llega un momento extraño.</p>
          </Reveal>
          <Reveal delay={200}>
            <div className="space-y-2 text-neutral-500 text-lg md:text-xl italic mb-12">
              <p>No necesariamente has tocado fondo.</p>
              <p>No necesariamente lo has perdido todo.</p>
            </div>
          </Reveal>
          <Reveal delay={400}>
            <p className="text-3xl md:text-5xl font-normal leading-[1.15] mb-4">Simplemente empiezas<br />a darte cuenta de algo:</p>
          </Reveal>
          <Reveal delay={700}>
            <p className="text-2xl md:text-4xl font-serif italic text-amber-700 leading-tight mt-6 mb-16">No quieres seguir viviendo así.</p>
          </Reveal>
          <Reveal delay={900}>
            <div className="border-t border-neutral-200 pt-12">
              <p className="text-neutral-500 mb-6 font-sans text-sm uppercase tracking-widest">Pero aparece otro problema</p>
              <p className="text-2xl font-serif text-neutral-900 mb-6">¿Y ahora qué hago?</p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-neutral-600 text-lg font-serif italic">
                <p>¿Voy a terapia?</p>
                <p>¿Me interno?</p>
                <p>¿Lo intento solo?</p>
                <p>¿Se lo cuento a mi familia?</p>
                <p>¿Dejo de juntarme con mis amigos?</p>
                <p>¿Puedo realmente hacerlo?</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ ACTO IV — YO TAMBIÉN ESTUVE AHÍ ============ */}
      <section className="relative bg-neutral-950 text-white py-24 overflow-hidden">
        <div className="grid md:grid-cols-2 items-center max-w-7xl mx-auto">
          <div className="relative h-[70vh] md:h-[85vh]">
            <img src={IMG.palmera} alt="Nelson González" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/80 via-transparent to-neutral-950/60 md:bg-gradient-to-r md:from-neutral-950 md:via-transparent md:to-transparent" />
          </div>
          <div className="px-8 md:px-12 py-16 md:py-0">
            <Reveal>
              <p className="uppercase tracking-[0.3em] text-[10px] text-amber-400 font-sans font-semibold mb-6">Sobre mí</p>
            </Reveal>
            <Reveal delay={150}>
              <h2 className="font-serif text-4xl md:text-5xl font-normal leading-tight mb-6">Yo también<br />estuve ahí.</h2>
            </Reveal>
            <Reveal delay={300}>
              <p className="font-serif text-xl text-neutral-300 mb-6">Mi nombre es <strong className="text-white">Nelson González.</strong></p>
            </Reveal>
            <Reveal delay={450}>
              <p className="font-sans text-neutral-300 text-base leading-relaxed mb-6">
                Durante aproximadamente <strong>20 años</strong> viví entre el alcohol y las drogas.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {['Alcohol', 'Cocaína', 'Ketamina', 'Pasta Base'].map((s) => (
                  <span key={s} className="border border-neutral-700 text-neutral-400 text-xs uppercase tracking-widest px-3 py-1.5 rounded-full font-sans">{s}</span>
                ))}
              </div>
              <p className="font-sans text-neutral-300 leading-relaxed">
                Intenté salir. Una vez. Y otra. Y otra.<br />
                Pasé por <strong className="text-white">cinco centros de rehabilitación.</strong> Conocí métodos, profesionales, terapias, reglas, recaídas, promesas y nuevas oportunidades.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ ACTO V — EL MAPA INVERSO + LIBRO ============ */}
      <section className="bg-[#faf7f2] text-neutral-900 py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMG.libro} alt="El Principio del Mapa Inverso — Nelson González" className="w-full max-w-md mx-auto rounded-lg shadow-2xl" />
          </Reveal>
          <div>
            <Reveal delay={200}>
              <p className="uppercase tracking-[0.3em] text-[10px] text-red-700 font-sans font-semibold mb-4">El libro</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-6">El Principio del<br /><em className="text-red-700 not-italic font-normal">Mapa Inverso.</em></h2>
            </Reveal>
            <Reveal delay={400}>
              <div className="space-y-4 font-serif text-lg text-neutral-700 leading-relaxed mb-8">
                <p>Durante mucho tiempo pensé que mi problema era simplemente dejar de consumir.</p>
                <p>Después entendí algo mucho más grande.</p>
                <p className="text-neutral-900"><strong>Tenía que reconstruir el mapa que me había llevado hasta ahí.</strong></p>
                <p className="italic text-neutral-600">Personas. Lugares. Hábitos. Rutinas. Pensamientos. Excusas. Decisiones. Entornos.</p>
                <p>Si existe un mapa para llegar hasta aquí… también podemos empezar a construir uno para salir.</p>
              </div>
            </Reveal>
            <Reveal delay={600}>
              <a
                href="https://elmapainverso.com/"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-colors px-6 py-3 rounded-full font-sans font-semibold text-sm"
                data-testid="libro-cta"
              >
                <BookOpen className="w-4 h-4" /> Conocer el libro <ExternalLink className="w-3 h-3" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ ACTO VI — PROGRAMA "EL PRIMER PASO" ============ */}
      <section id="programa" className="bg-white py-24 px-6 scroll-mt-20">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-[10px] text-amber-700 font-sans font-semibold mb-4">Programa El Primer Paso</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-6">Ahí es donde quiero<br />acompañarte.</h2>
            <p className="font-sans text-neutral-600 text-base leading-relaxed max-w-2xl mx-auto">
              No es una cura. No es terapia. No es un tratamiento mágico. Es un espacio para ayudarte a pasar de <em>&ldquo;tengo que cambiar&rdquo;</em> a <strong>&ldquo;estoy empezando a cambiar&rdquo;</strong>.
            </p>
          </Reveal>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-4 gap-3 mb-20">
          {[
            { icon: <Compass className="w-5 h-5" />, title: 'Conciencia', desc: 'Entender dónde estás y reconocer tus patrones.' },
            { icon: <Target className="w-5 h-5" />, title: 'Dirección', desc: 'Definir qué cambiar y qué camino tiene sentido para ti.' },
            { icon: <TrendingUp className="w-5 h-5" />, title: 'Acción', desc: 'Transformarlo en decisiones y acciones medibles.' },
            { icon: <Sparkles className="w-5 h-5" />, title: 'Seguimiento', desc: 'Porque el cambio ocurre durante el resto de la semana.' },
          ].map((step, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-5 h-full">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center">{step.icon}</div>
                  <span className="text-xs text-neutral-500 font-sans">0{i+1}</span>
                </div>
                <h3 className="font-serif text-lg font-normal mb-2">{step.title}</h3>
                <p className="font-sans text-sm text-neutral-600 leading-relaxed">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h3 className="font-serif text-2xl md:text-3xl font-normal mb-8 text-center">¿Qué incluye el programa?</h3>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Acompañamiento durante el proceso', desc: 'Seguimiento de hábitos y compromisos entre sesiones.' },
              { title: 'Videollamadas', desc: 'Para revisar avances, dificultades y próximos pasos.' },
              { title: 'Sesiones grupales', desc: 'Con personas atravesando procesos similares.' },
              { title: 'Herramientas digitales', desc: 'Para visualizar objetivos, hábitos y progreso.' },
              { title: 'Psicoeducación', desc: 'Consumo, hábitos, disparadores, motivación, cambio.' },
              { title: 'Seguimiento posterior', desc: 'Porque una conversación puede ayudarte, pero el cambio ocurre después.' },
            ].map((it, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="flex gap-4 border-b border-neutral-100 pb-5">
                  <div className="w-1 bg-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-serif text-lg font-normal mb-1">{it.title}</p>
                    <p className="font-sans text-sm text-neutral-600">{it.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ACTO VII — QUIÉN TE ACOMPAÑA ============ */}
      <section id="sobre-nelson" className="bg-[#0f172a] text-white py-24 px-6 scroll-mt-20">
        <div className="max-w-5xl mx-auto grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2">
            <Reveal>
              <img src={IMG.coffee} alt="Nelson González" className="rounded-2xl w-full shadow-2xl" />
            </Reveal>
          </div>
          <div className="md:col-span-3">
            <Reveal delay={200}>
              <p className="uppercase tracking-[0.3em] text-[10px] text-amber-400 font-sans font-semibold mb-4">Quién te acompaña</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-6">Nelson González</h2>
            </Reveal>
            <Reveal delay={400}>
              <ul className="space-y-2.5 font-sans text-neutral-300 mb-8">
                <li className="flex gap-2"><span className="text-amber-400">•</span> Coach y especialista en construcción de hábitos</li>
                <li className="flex gap-2"><span className="text-amber-400">•</span> Autor de <em>El Principio del Mapa Inverso</em></li>
                <li className="flex gap-2"><span className="text-amber-400">•</span> Fundador de SinAdicciones.org</li>
                <li className="flex gap-2"><span className="text-amber-400">•</span> Empresario digital · Persona en recuperación</li>
                <li className="flex gap-2"><span className="text-amber-400">•</span> Padre de 3 hijos</li>
                <li className="flex gap-2"><span className="text-amber-400">•</span> Colaborador de <strong className="text-white">SENDA Previene Puente Alto</strong></li>
                <li className="flex gap-2"><span className="text-amber-400">•</span> Realiza charlas en colegios sobre prevención y consumo</li>
              </ul>
            </Reveal>
            <Reveal delay={600}>
              <div className="border border-amber-600/30 bg-amber-500/5 rounded-xl p-5 font-sans text-sm text-neutral-300 leading-relaxed">
                <p className="text-amber-300 font-semibold mb-2 text-xs uppercase tracking-widest">Importante</p>
                No soy psicólogo. No soy terapeuta. No soy psiquiatra. No diagnostico ni reemplazo un tratamiento profesional. Mi experiencia personal no me convierte en profesional clínico. Pero sí me permite sentarme frente a alguien que está viviendo algo que conozco muy bien.
              </div>
            </Reveal>
          </div>
        </div>

        {/* Prueba social — podcast + colegio */}
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-4 mt-14">
          <Reveal>
            <div className="relative rounded-xl overflow-hidden aspect-[4/5] sm:aspect-video">
              <img src={IMG.podcast} alt="Podcast" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 right-4 font-sans text-xs uppercase tracking-widest text-white/90">Entrevistas y podcasts</p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative rounded-xl overflow-hidden aspect-[4/5] sm:aspect-video">
              <img src={IMG.colegio} alt="Charla en colegios" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 right-4 font-sans text-xs uppercase tracking-widest text-white/90">Charlas en colegios · SENDA Previene</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ ACTO VIII — HERRAMIENTAS GRATUITAS ============ */}
      <section id="herramientas" className="bg-white py-24 px-6 scroll-mt-20">
        <div className="max-w-5xl mx-auto text-center mb-14">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-[10px] text-amber-700 font-sans font-semibold mb-4">Antes de decidir</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-4">Empieza con algo gratis</h2>
            <p className="font-sans text-neutral-600">Herramientas que puedes usar hoy mismo, sin compromiso.</p>
          </Reveal>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-5">
          <Reveal>
            <button
              onClick={() => setTestOpen(true)}
              className="text-left w-full bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-xl p-6 transition-colors group"
              data-testid="cta-test-dep"
            >
              <div className="w-11 h-11 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center mb-4">
                <Target className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl mb-2">Test de Dependencia</h3>
              <p className="font-sans text-sm text-neutral-600 mb-4">Escala clínica DAST-10. 10 preguntas, 3 minutos.</p>
              <span className="font-sans text-sm text-amber-700 font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Hacer el test <ArrowRight className="w-3 h-3" /></span>
            </button>
          </Reveal>
          <Reveal delay={100}>
            <Link
              to="/reto-21-dias"
              className="block bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-xl p-6 transition-colors group"
              data-testid="cta-reto21"
            >
              <div className="w-11 h-11 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl mb-2">Reto 21 Días</h3>
              <p className="font-sans text-sm text-neutral-600 mb-4">Programa gratuito con acompañamiento diario por email.</p>
              <span className="font-sans text-sm text-emerald-700 font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Unirme al reto <ArrowRight className="w-3 h-3" /></span>
            </Link>
          </Reveal>
          <Reveal delay={200}>
            <a
              href="https://elmapainverso.com/"
              target="_blank"
              rel="noreferrer noopener"
              className="block bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-xl p-6 transition-colors group"
              data-testid="cta-libro"
            >
              <div className="w-11 h-11 bg-red-100 text-red-700 rounded-full flex items-center justify-center mb-4">
                <BookOpen className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl mb-2">El Mapa Inverso</h3>
              <p className="font-sans text-sm text-neutral-600 mb-4">Lee el libro completo o descarga el primer capítulo.</p>
              <span className="font-sans text-sm text-red-700 font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Descubrir el libro <ExternalLink className="w-3 h-3" /></span>
            </a>
          </Reveal>
        </div>
        <div className="text-center mt-10">
          <Link to="/herramientas" className="font-sans text-sm text-neutral-500 hover:text-neutral-900 underline underline-offset-4">
            Ver todas las herramientas gratuitas →
          </Link>
        </div>
      </section>

      {/* ============ ACTO IX — CIERRE + PRECIO + WSP ============ */}
      <section id="cta" className="bg-gradient-to-br from-amber-900 via-neutral-900 to-neutral-950 text-white py-24 px-6 scroll-mt-20">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-[10px] text-amber-400 font-sans font-semibold mb-6">Programa El Primer Paso</p>
          </Reveal>
          <Reveal delay={150}>
            <h2 className="font-serif text-4xl md:text-6xl font-light leading-[1.1] mb-8">
              No necesitas prometer que<br />nunca más vas a consumir.
            </h2>
          </Reveal>
          <Reveal delay={300}>
            <div className="font-serif text-xl md:text-2xl text-neutral-300 mb-2">Hoy solo necesitas decidir:</div>
            <div className="font-serif text-2xl md:text-3xl text-white italic mb-1">¿Voy a seguir haciendo lo mismo?</div>
            <div className="font-sans text-neutral-500 text-sm mb-1">o</div>
            <div className="font-serif text-2xl md:text-3xl text-amber-400 italic mb-12">¿Voy a empezar a moverme?</div>
          </Reveal>
          <Reveal delay={500}>
            <div className="inline-block bg-white/5 border border-white/10 rounded-2xl px-10 py-8 mb-10 backdrop-blur">
              <p className="text-sm text-neutral-400 uppercase tracking-widest font-sans mb-3">Programa mensual</p>
              <div className="font-serif text-2xl md:text-3xl font-light text-white mb-2 leading-snug">Para más información<br/>y precios, contáctame</div>
              <p className="text-sm text-neutral-400 font-sans mt-3">Acompañamiento · Grupo · Herramientas</p>
            </div>
          </Reveal>
          <Reveal delay={700}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <a
                href={WSP_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#1eb85a] text-white px-8 py-4 rounded-full font-sans font-semibold text-base transition-colors"
                data-testid="cta-wsp-main"
              >
                <MessageCircle className="w-5 h-5" /> Dar mi primer paso
              </a>
              <button
                onClick={() => setTestOpen(true)}
                className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-8 py-4 rounded-full font-sans font-semibold text-base transition-colors"
                data-testid="cta-test-secondary"
              >
                ¿Es para mí? Hacer test
              </button>
            </div>
          </Reveal>
          <Reveal delay={900}>
            <p className="mt-14 text-[11px] text-neutral-500 font-sans leading-relaxed max-w-xl mx-auto">
              Programa de coaching, acompañamiento y educación. No constituye tratamiento médico, psicológico, psiquiátrico ni terapia de adicciones. Cuando corresponda, se recomendará evaluación y atención profesional especializada.
            </p>
          </Reveal>
        </div>
      </section>

      <CoachFooter />

      {/* Floating WhatsApp — Desktop */}
      <a
        href={WSP_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="hidden md:flex fixed bottom-6 right-6 z-40 bg-[#25d366] hover:bg-[#1eb85a] text-white w-14 h-14 rounded-full shadow-2xl items-center justify-center transition-transform hover:scale-105"
        aria-label="WhatsApp"
        data-testid="floating-wsp-desktop"
      >
        <MessageCircle className="w-6 h-6" />
      </a>

      {/* Sticky bar — Mobile */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-200 shadow-2xl px-4 py-3">
        <a
          href={WSP_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="flex items-center justify-center gap-2 bg-[#25d366] hover:bg-[#1eb85a] text-white w-full py-3.5 rounded-full font-sans font-semibold text-sm"
          data-testid="sticky-wsp-mobile"
        >
          <MessageCircle className="w-4 h-4" /> Quiero saber más
        </a>
      </div>

      <DependenciaTestModal open={testOpen} onClose={() => setTestOpen(false)} onCtaClick={() => { setTestOpen(false); window.open(WSP_URL, '_blank', 'noreferrer'); }} />
    </div>
  );
}

/* -------------------- HEADER -------------------- */
function CoachHeader({ onCta }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const nav = (id, label) => (
    <a
      href={`#${id}`}
      onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
      className={`transition-colors hover:opacity-100 ${scrolled ? 'text-neutral-700 hover:text-neutral-900' : 'text-white/80 hover:text-white'}`}
    >
      {label}
    </a>
  );
  return (
    <header className={`fixed top-0 left-0 right-0 z-30 transition-all ${scrolled ? 'bg-white/95 backdrop-blur border-b border-neutral-200' : 'bg-transparent'}`} data-testid="coach-header">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-4">
        <Link to="/" className={`font-serif text-lg tracking-tight ${scrolled ? 'text-neutral-900' : 'text-white'}`}>
          Nelson<span className="text-amber-500">.</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 font-sans text-sm">
          {nav('programa', 'Programa')}
          {nav('sobre-nelson', 'Sobre Nelson')}
          {nav('herramientas', 'Herramientas gratuitas')}
        </nav>
        <button
          onClick={onCta}
          className="bg-[#25d366] hover:bg-[#1eb85a] text-white px-5 py-2 rounded-full font-sans font-semibold text-xs md:text-sm flex items-center gap-1.5 transition-colors"
          data-testid="header-wsp"
        >
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </button>
      </div>
    </header>
  );
}

/* -------------------- FOOTER -------------------- */
function CoachFooter() {
  return (
    <footer className="bg-neutral-950 text-neutral-400 py-12 px-6 font-sans text-sm pb-24 md:pb-12">
      <div className="max-w-4xl mx-auto text-center space-y-3">
        <p className="text-white font-serif text-lg">Nelson González</p>
        <p>Coach en adicciones · Fundador SinAdicciones.org · Chile</p>
        <p className="text-xs text-neutral-500 pt-4 border-t border-neutral-900 mt-6">
          <Link to="/" className="hover:text-neutral-300">SinAdicciones.org</Link> · <Link to="/blog" className="hover:text-neutral-300">Blog</Link> · <Link to="/herramientas" className="hover:text-neutral-300">Herramientas</Link> · <Link to="/tratamiento-adicciones-online" className="hover:text-neutral-300">Tratamiento Online</Link>
        </p>
        <p className="text-[10px] text-neutral-600">Emergencia 24/7 · Fonodrogas SENDA: <strong>1412</strong></p>
      </div>
    </footer>
  );
}
